import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../../api/client'
import type { ContainerPourResult, ProductContainer, ProposalDetail } from '../../api/types'
import { Button, Num, formatDateTime, plural } from '../../ui/primitives'

/**
 * Riversa in proposta un contenitore compilato col palmare.
 *
 * <p>L'addetto gira fra gli scaffali e spara i codici di ciò che manca; qui quel giro diventa
 * quantità aggiunta, in un gesto invece che in trenta righe digitate.</p>
 *
 * <p>Il pannello non compare se non ci sono contenitori disponibili — e non compare nemmeno
 * quando l'integrazione col sistema di negozio non è configurata, perché il server risponde con
 * un elenco vuoto in entrambi i casi. Un pulsante che apre sempre il vuoto è peggio di un
 * pulsante assente.</p>
 */
export function ContainerPanel({
  proposalId,
  onPoured,
}: {
  proposalId: number
  onPoured: (detail: ProposalDetail) => void
}) {
  const [containers, setContainers] = useState<ProductContainer[]>()
  const [open, setOpen] = useState(false)
  const [pouring, setPouring] = useState<string>()
  const [result, setResult] = useState<ContainerPourResult>()
  const [error, setError] = useState<string>()

  const load = useCallback(() => {
    api
      .get<ProductContainer[]>(`/order-proposals/${proposalId}/containers`)
      .then(setContainers)
      // Un errore qui non deve rovinare la pagina: la proposta si lavora lo stesso.
      .catch(() => setContainers([]))
  }, [proposalId])

  useEffect(load, [load])

  const pour = (container: ProductContainer) => {
    if (
      !window.confirm(
        [
          `Riversare il contenitore «${container.name}» in questa proposta?`,
          '',
          `${plural(container.lineCount, 'articolo', 'articoli')}, ${container.totalQuantity} pezzi.`,
          'Gli articoli non ordinabili verranno saltati e segnalati.',
        ].join('\n'),
      )
    )
      return

    setPouring(container.id)
    setError(undefined)
    setResult(undefined)

    api
      .post<ContainerPourResult>(`/order-proposals/${proposalId}/containers/${container.id}`)
      .then((r) => {
        setResult(r)
        onPoured(r.proposal)
        load()
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? (e.detail ?? e.title) : 'Riversamento non riuscito.'),
      )
      .finally(() => setPouring(undefined))
  }

  // Finché non si sa, e quando non ce ne sono, il pannello non esiste.
  if (containers === undefined) return null
  if (containers.length === 0 && !result && !error) return null

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        title="Riversa in proposta un giro fatto col palmare"
      >
        Riversa contenitore{' '}
        {containers.length > 0 && <span className="badge badge-info">{containers.length}</span>}
      </Button>
    )
  }

  return (
    <div className="addline">
      <div className="addline-head">
        <strong>Contenitori dal palmare</strong>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Chiudi
        </Button>
      </div>

      {result && (
        <div className="notice notice-ok">
          <strong>Contenitore riversato</strong>
          <p>
            <Num value={result.linesAdded} />{' '}
            {result.linesAdded === 1 ? 'articolo entrato' : 'articoli entrati'} in proposta,{' '}
            <Num value={result.totalQuantity} /> pezzi.
          </p>

          {result.skipped.length > 0 && (
            <>
              <p>
                <Num value={result.linesSkipped} />{' '}
                {result.linesSkipped === 1 ? 'articolo è rimasto fuori' : 'articoli sono rimasti fuori'}:
              </p>
              <ul className="container-skipped">
                {result.skipped.map((s) => (
                  <li key={s.itemCode}>
                    <span className="num">{s.itemCode}</span>
                    {s.description ? ` ${s.description}` : ''} — {s.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {error && <div className="notice notice-error">{error}</div>}

      {containers.length === 0 ?
        <div className="muted">
          Nessun altro contenitore da riversare in questa proposta.
        </div>
      : <div className="container-list">
          {containers.map((c) => (
            <div key={c.id} className="container-item">
              <div className="container-item-main">
                <strong>{c.name}</strong>
                <small className="muted">
                  {plural(c.lineCount, 'articolo', 'articoli')} · {c.totalQuantity} pezzi
                  {c.compiledBy ? ` · ${c.compiledBy}` : ''}
                  {c.lastUpdatedAt ? ` · ${formatDateTime(c.lastUpdatedAt)}` : ''}
                </small>
              </div>
              <Button onClick={() => pour(c)} disabled={pouring !== undefined}>
                {pouring === c.id ? 'Riversamento…' : 'Riversa'}
              </Button>
            </div>
          ))}
        </div>
      }

      {containers.length > 0 && (
        <div className="addline-note muted">
          Le quantità entrano come «aggiunta» e vengono arrotondate alla confezione. Un contenitore
          si riversa una volta sola.
        </div>
      )}
    </div>
  )
}
